import {useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject} from 'react';
import {flushSync} from 'react-dom';
import {createGenieAnimation, type GenieAnimationController, type GenieRect} from '../genie/genie';
import {captureGenieSnapshot} from '../genie/snapshot';

/* ===========================================================================
   The phone equivalent of `desktop/windowMotion.ts`.
   ---------------------------------------------------------------------------
   On the desktop a window flies between its frame and the parking slot beside
   the dock. A phone has no slot — but it has the thing the user actually
   touched: an app icon on the home screen, in the touch dock, or a row in
   "Đang mở". So the sheet flies between the full screen and THAT box, and the
   two machines are the same two machines as on the desktop:

     hero    a transform flight (transform + opacity only) — used to open an app
             for the first time, and whenever the genie has nothing to work with
     genie   the real warp: the sheet is rasterised once and funnelled into the
             icon, then unfolded back out of it when the app is resumed

   Same doctrine as the desktop: the genie is an ADDITION. If the bitmap is not
   ready, or the icon cannot be measured, the sheet still flies — it just flies
   with a transform. There is no branch in which an app disappears without a
   trace, and `prefers-reduced-motion` removes all of it.
   =========================================================================== */

const OPEN_MS = 300;
const FOLD_MS = 260;
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_IN = 'cubic-bezier(0.55, 0, 0.85, 0.4)';

/** A warm bitmap is only the truth for a moment; after that, capture again. */
const WARM_TTL_MS = 4000;
/**
 * How long a dismiss will wait for the bitmap before giving up on the genie.
 *
 * The desktop waits as long as it takes, because a pointer hovering the
 * minimise button has already paid for the rasterise. A finger cannot hover:
 * pointer-down and click can be the same instant, so the wait is real and it is
 * capped at roughly one fold. Measured here at ~90ms cold for a phone-sized
 * sheet, so the cap is rarely reached — and when it is, the app still flies
 * into its icon, just with a transform.
 */
const CAPTURE_BUDGET_MS = 260;
/** A parked app can be resumed much later, but not from a picture of last week. */
const PARKED_TTL_MS = 120_000;

/** Corner radius the sheet folds down to, matching the icon tile's own. */
const ICON_RADIUS = '22px';

interface Box {
  height: number;
  left: number;
  top: number;
  width: number;
}

function reduceMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function boxOf(element: Element): Box | null {
  const rect = element.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return null;
  return {height: rect.height, left: rect.left, top: rect.top, width: rect.width};
}

function toGenieRect(box: Box): GenieRect {
  return {height: box.height, left: box.left, top: box.top, width: box.width};
}

/* ------------------------------------------------------------- the origin  */

/**
 * Which icon this app flies out of and back into.
 *
 * The element the user actually touched is remembered on the way in, because
 * the same app can be launched from three places at once (home grid, touch
 * dock, the "Đang mở" list) and only one of them is the one they aimed at. If
 * that element is gone or has scrolled off screen by the time the app is put
 * away — a long home screen, a rotated phone — any visible launcher for the
 * same app will do, and the flight simply aims somewhere honest instead of
 * flying to a point outside the window.
 */
interface Launch {
  at: number;
  /** Where the icon was when the finger landed on it. */
  box: Box | null;
  element: HTMLElement;
}

const touched = new Map<string, Launch>();

/** How long a touch-time measurement is still the place the user aimed at. */
const LAUNCH_BOX_TTL_MS = 1500;

export function rememberLaunchOrigin(appId: string, element: HTMLElement | null): void {
  if (element) touched.set(appId, {at: performance.now(), box: boxOf(element), element});
}

/** An icon scrolled off the home screen is not somewhere to fly to. */
function onScreen(box: Box): boolean {
  return box.top < window.innerHeight - 8 && box.top + box.height > 8;
}

/**
 * `launch` asks for the box the user actually touched, rather than where that
 * icon has since ended up.
 *
 * Opening an app adds it to "Đang mở", which pushes the whole app grid down by
 * a section — so by the time the flight is set up, the icon has moved ~130px
 * from under the finger that started it. Flying out of the new position is
 * measurably correct and visibly wrong: the app has to grow out of the place
 * the user pressed. Putting it away is the opposite case — the home screen is
 * settled and visible, so the destination is measured fresh.
 */
function originFor(appId: string, phase: 'launch' | 'return'): Box | null {
  const remembered = touched.get(appId);

  if (
    phase === 'launch' &&
    remembered?.box &&
    performance.now() - remembered.at < LAUNCH_BOX_TTL_MS
  ) {
    return remembered.box;
  }

  if (remembered?.element.isConnected) {
    const box = boxOf(remembered.element);
    if (box && onScreen(box)) return box;
  }

  const nodes = document.querySelectorAll<HTMLElement>(`[data-app-icon="${CSS.escape(appId)}"]`);
  for (const node of nodes) {
    const box = boxOf(node);
    if (box && onScreen(box)) return box;
  }
  return null;
}

/* ------------------------------------------------------------- the bitmap  */

interface Snapshot {
  at: number;
  canvas: HTMLCanvasElement;
}

/** Bitmaps of parked apps, waiting to be unfolded when the app is resumed. */
const parkedShots = new Map<string, Snapshot>();

/**
 * Apps that cost more to photograph than the photograph is worth.
 *
 * The genie needs one rasterised copy of the app. That is cheap for a form or a
 * table — measured at ~90ms — and it is NOT cheap for a screen full of images:
 * a listing grid with nine photos measured at over a second, because every
 * picture has to be inlined into the snapshot. Spending a second of main thread
 * to enable a 450ms effect is a bad trade in any direction you read it, and
 * repeating it on every dismiss is worse.
 *
 * So the cost is measured once, at idle, and an app that goes over budget is
 * never rasterised again in this session. It still flies into its icon — with
 * the transform machine, which costs nothing and is smooth on the same phones
 * where the raster would not be. This is the same doctrine as the desktop, held
 * to honestly: the genie is an addition, and an addition that hurts is dropped.
 */
const tooExpensive = new Set<string>();
const RASTER_BUDGET_MS = 320;

/**
 * Look at the parked bitmap WITHOUT consuming it.
 *
 * Consuming it on read is the obvious version and it is wrong: StrictMode runs
 * the open effect twice (mount → cleanup → mount), so the first pass would eat
 * the picture, the cleanup would cancel its flight, and the second pass would
 * find nothing and fall back to a plain hero. The bitmap is dropped when a
 * flight actually finishes with it, not when one starts.
 */
function peekParkedShot(appId: string): HTMLCanvasElement | null {
  const shot = parkedShots.get(appId);
  if (!shot) return null;
  if (performance.now() - shot.at > PARKED_TTL_MS) {
    parkedShots.delete(appId);
    return null;
  }
  return shot.canvas;
}

export function forgetParkedShot(appId: string): void {
  parkedShots.delete(appId);
}

export interface SheetMotion {
  /** True while the genie overlay owns the pixels; the sheet must not paint. */
  parked: boolean;
  /**
   * Rasterise now, so the flight can start on the next frame.
   *
   * The touch equivalent of the desktop's hover warm-up: a finger landing on
   * the close button — or on the grabber to start a dismiss drag — is a promise
   * that the app is about to be put away, and the finger is holding still on a
   * 44px target while the work happens.
   */
  prewarm: () => void;
  /** Fly the sheet into its icon, then run `action` (park or close). */
  dismiss: (action: () => void, options?: {keep?: boolean}) => void;
}

export function useSheetMotion(
  appId: string,
  sheetRef: RefObject<HTMLDivElement | null>,
): SheetMotion {
  const [parked, setParked] = useState(false);
  const warmRef = useRef<Snapshot | null>(null);
  const capturingRef = useRef<Promise<HTMLCanvasElement | null> | null>(null);
  const genieRef = useRef<GenieAnimationController | null>(null);
  const animationRef = useRef<Animation | null>(null);
  const leavingRef = useRef(false);

  const foldTransform = useCallback((element: HTMLElement, box: Box) => {
    // Layout size, not the measured rect: the drag gesture may have a transform
    // on the element, and a fold computed from a transformed box compounds it.
    const width = element.offsetWidth || window.innerWidth;
    const height = element.offsetHeight || window.innerHeight;
    const scaleX = Math.max(0.02, box.width / width);
    const scaleY = Math.max(0.02, box.height / height);
    return `translate3d(${box.left}px, ${box.top}px, 0) scale(${scaleX}, ${scaleY})`;
  }, []);

  /* ---------------------------------------------------------------- open  */

  useLayoutEffect(() => {
    const element = sheetRef.current;
    if (!element) return undefined;

    if (reduceMotion()) {
      element.setAttribute('data-plain', '');
      return undefined;
    }

    const box = originFor(appId, 'launch');
    const canvas = peekParkedShot(appId);

    if (!box) {
      // Nothing to fly out of: fall back to the plain rise, which is still an
      // arrival rather than a hard cut.
      element.setAttribute('data-plain', '');
      return undefined;
    }

    if (canvas) {
      /* The app was parked with the genie, so it comes back the same way: the
         overlay unfolds the bitmap out of the icon while the real sheet stays
         invisible underneath, and the hand-off happens behind a picture of
         itself. */
      const controller = createGenieAnimation({
        direction: 'restore',
        originalRect: {
          height: element.offsetHeight || window.innerHeight,
          left: 0,
          top: 0,
          width: element.offsetWidth || window.innerWidth,
        },
        ownerDocument: document,
        position: 'bottom',
        sourceCanvas: canvas,
        targetRect: toGenieRect(box),
      });

      if (controller) {
        genieRef.current = controller;
        // `opacity`, never `visibility`: an invisible-but-present sheet still
        // receives touches, so a finger that lands during the 450ms unfold hits
        // the app that is arriving instead of falling through to the home
        // screen behind it and launching something else.
        element.style.opacity = '0';
        void controller
          .play({
            beforeFinish: async () => {
              element.style.opacity = '';
              await nextFrame();
            },
          })
          .then(() => {
            genieRef.current = null;
            // The picture has been spent; the next open starts from the icon.
            parkedShots.delete(appId);
          });

        return () => {
          element.style.opacity = '';
          genieRef.current?.cancel();
          genieRef.current = null;
        };
      }
    }

    // First open: the hero flight. Only `transform` and `opacity` are animated
    // — the two properties the compositor can run without touching layout, so a
    // list of two hundred rows does not re-lay-out sixty times a second.
    const animation = element.animate(
      [
        {borderRadius: ICON_RADIUS, offset: 0, opacity: 0, transform: foldTransform(element, box)},
        {offset: 0.45, opacity: 1},
        {borderRadius: '0px', offset: 1, opacity: 1, transform: 'none'},
      ],
      {duration: OPEN_MS, easing: EASE_OUT, fill: 'backwards'},
    );
    animationRef.current = animation;

    return () => {
      animation.cancel();
      animationRef.current = null;
    };
    // Mount only. Under StrictMode this runs, is cancelled, and runs again —
    // which is right: the second pass replays the flight from the start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------------------------------------ snapshots  */

  const warmShot = useCallback(() => {
    const warm = warmRef.current;
    if (!warm) return null;
    if (performance.now() - warm.at > WARM_TTL_MS) {
      warmRef.current = null;
      return null;
    }
    return warm;
  }, []);

  /**
   * The bitmap, however it has to be obtained: already warm, already on its
   * way, or captured right now. One capture at a time — a finger that lands on
   * the grabber and then on the close button must not start two.
   */
  const capture = useCallback((): Promise<HTMLCanvasElement | null> => {
    const element = sheetRef.current;
    if (!element || reduceMotion()) return Promise.resolve(null);
    // Mid-flight the sheet is hidden underneath the overlay; rasterising it now
    // would bake a blank picture and the next flight would funnel nothing.
    if (genieRef.current) return Promise.resolve(null);
    if (tooExpensive.has(appId)) return Promise.resolve(null);
    const warm = warmShot();
    if (warm) return Promise.resolve(warm.canvas);
    if (capturingRef.current) return capturingRef.current;

    const startedAt = performance.now();
    const job = captureGenieSnapshot(element).then(
      (snapshot) => {
        capturingRef.current = null;
        if (!snapshot) return null;
        if (performance.now() - startedAt > RASTER_BUDGET_MS) {
          // Too slow to be worth it — and the answer will not change on the
          // next attempt, so stop asking.
          tooExpensive.add(appId);
          return null;
        }
        warmRef.current = {at: performance.now(), canvas: snapshot.canvas};
        return snapshot.canvas;
      },
      () => {
        capturingRef.current = null;
        return null;
      },
    );
    capturingRef.current = job;
    return job;
  }, [appId, sheetRef, warmShot]);

  const prewarm = useCallback(() => {
    if (!leavingRef.current) void capture();
  }, [capture]);

  /**
   * Keep a fresh picture on hand while the app just sits there.
   *
   * On the desktop the rasterise is paid for by a pointer hovering the minimise
   * button. A finger cannot hover, and the heavier the app the less likely a
   * tap is to cover the cost — precisely backwards, since the heaviest apps are
   * the ones whose disappearance is most jarring. So the picture is taken while
   * the phone is idle instead, and thrown away as soon as it stops being true:
   * scrolling a list changes what the app looks like, and a genie that funnels
   * a picture of the wrong scroll position is worse than no genie.
   */
  useEffect(() => {
    const element = sheetRef.current;
    if (!element || reduceMotion()) return undefined;

    let timer: number | null = null;
    let idle: number | null = null;

    const run = () => {
      idle = null;
      if (!leavingRef.current) void capture();
    };

    const schedule = (delay: number) => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        idle =
          typeof requestIdleCallback === 'function'
            ? requestIdleCallback(run, {timeout: 1200})
            : window.setTimeout(run, 0);
      }, delay);
    };

    const invalidate = () => {
      warmRef.current = null;
      schedule(500);
    };

    // Long enough for the opening flight to land first: the capture is a long
    // task, and a long task during the flight is the one place it would show.
    schedule(800);
    element.addEventListener('scroll', invalidate, {capture: true, passive: true});

    return () => {
      element.removeEventListener('scroll', invalidate, {capture: true});
      if (timer !== null) window.clearTimeout(timer);
      if (idle !== null) {
        if (typeof cancelIdleCallback === 'function') cancelIdleCallback(idle);
        else window.clearTimeout(idle);
      }
    };
  }, [capture, sheetRef]);

  /* --------------------------------------------------------------- leave  */

  const foldWithTransform = useCallback(
    (element: HTMLElement, box: Box | null, action: () => void) => {
      const to = box
        ? {borderRadius: ICON_RADIUS, opacity: 0, transform: foldTransform(element, box)}
        : {opacity: 0, transform: 'translate3d(0, 100%, 0)'};

      const animation = element.animate(
        [{borderRadius: '0px', opacity: 1, transform: getComputedStyle(element).transform}, to],
        {duration: FOLD_MS, easing: EASE_IN, fill: 'forwards'},
      );
      animationRef.current = animation;
      animation.onfinish = () => {
        animationRef.current = null;
        action();
      };
      // A cancelled animation (unmount mid-flight) must still hand over.
      animation.oncancel = () => {
        animationRef.current = null;
      };
    },
    [foldTransform],
  );

  const dismiss = useCallback<SheetMotion['dismiss']>(
    (action, options) => {
      if (leavingRef.current) return;
      leavingRef.current = true;

      const element = sheetRef.current;
      if (!element || reduceMotion()) {
        action();
        return;
      }

      // Put away while it is still arriving. The incoming flight is abandoned
      // where it stands and the sheet is handed back its own pixels first —
      // otherwise the outgoing flight would start from an element that is still
      // invisible, and the app would appear to vanish instead of fold.
      if (genieRef.current) {
        genieRef.current.cancel();
        genieRef.current = null;
        element.style.opacity = '';
      }
      animationRef.current?.cancel();
      animationRef.current = null;

      void (async () => {
        const canvas = await Promise.race([
          capture(),
          new Promise<null>((resolve) => window.setTimeout(() => resolve(null), CAPTURE_BUDGET_MS)),
        ]);
        warmRef.current = null;

        // Measured AFTER the wait, never before: the home screen behind the
        // sheet may have re-laid-out (an app just joined "Đang mở"), and a
        // funnel aimed at where the icon used to be is worse than no funnel.
        const box = originFor(appId, 'return');

        if (!canvas || !box) {
          foldWithTransform(element, box, action);
          return;
        }

        // The bitmap is the sheet at its natural size; where it currently SITS
        // may differ, because a dismiss drag has already moved it. Measure now
        // and the funnel starts from exactly where the finger left it.
        const from = boxOf(element);
        const controller = from
          ? createGenieAnimation({
              direction: 'minimize',
              originalRect: toGenieRect(from),
              ownerDocument: document,
              position: 'bottom',
              sourceCanvas: canvas,
              targetRect: toGenieRect(box),
            })
          : null;

        if (!controller) {
          foldWithTransform(element, box, action);
          return;
        }

        genieRef.current = controller;
        // Frame 0 of the overlay is an exact copy of the sheet, in the sheet's
        // own place, so hiding the real one underneath it is invisible.
        // `flushSync` because that must be true of THIS frame, not of whichever
        // frame React would have picked.
        flushSync(() => setParked(true));

        await controller.play({
          beforeFinish: async () => {
            // Parking keeps the picture so the app can unfold back out of the
            // icon; closing throws it away, because a closed app should open
            // fresh rather than out of a memory of itself.
            if (options?.keep !== false) {
              parkedShots.set(appId, {at: performance.now(), canvas});
            }
            await nextFrame();
          },
        });

        genieRef.current = null;
        action();
      })();
    },
    [appId, capture, foldWithTransform, sheetRef],
  );

  useLayoutEffect(
    () => () => {
      genieRef.current?.cancel();
      genieRef.current = null;
      animationRef.current?.cancel();
      animationRef.current = null;
    },
    [],
  );

  return {dismiss, parked, prewarm};
}
